import type { SectionKind } from "./types";

type BodySectionKind = Exclude<SectionKind, "custom" | "default">;

const BODY_SECTION_ALIASES: Record<BodySectionKind, readonly string[]> = {
  background: [
    "Beruflicher Weg", "Creative journey", "Development experience", "Entwicklungserfahrung", "Experiencia en desarrollo", "Experiência em desenvolvimento", "Kreativer Werdegang", "Mein Werdegang", "Mi trayectoria", "Minha trajetória", "Mon parcours", "My background", "Parcours créatif", "Parcours de développement", "Parcours professionnel", "Percurso criativo", "Professional experience", "Recorrido creativo", "Trajetória profissional", "Trayectoria profesional", "Мой путь", "Опыт разработки", "Профессиональный опыт", "Творческий путь", "المسيرة المهنية", "خبرة التطوير", "رحلتي الإبداعية", "مسيرتي", "これまでの経験", "从业经历", "仕事の経歴", "创作经历", "創作の歩み", "創作經歷", "开发经历", "從業經歷", "我的經歷", "我的经历", "開発の経歴", "開發經歷", "개발 경력", "나의 경험", "실무 경력", "창작 이력",
    "关于我", "我的专长", "關於我", "我的專長", "About me", "My expertise",
    "私について", "得意分野", "소개", "전문 분야", "Sobre mí", "Mi especialidad",
    "À propos", "Mon expertise", "Über mich", "Meine Expertise", "Sobre mim",
    "Minha especialidade", "Обо мне", "Моя специализация", "نبذة عني", "خبرتي"
  ],
  now: [
    "Creando ahora", "Creating now", "Criando agora", "En création", "Gerade in Arbeit", "Mein Arbeitsfeld", "Mi actividad", "Minha atuação", "Mon activité", "My practice", "Моя практика", "Сейчас создаю", "ما أصنعه الآن", "مجال عملي", "いま制作中", "仕事の軸", "服务方向", "服務方向", "正在创作", "正在創作", "주요 업무", "지금 창작하는 것",
    "正在做", "最近更新", "What I'm doing", "In progress", "Latest updates",
    "今していること", "開発中", "最近の活動", "지금 하는 일", "지금 만드는 것",
    "최근 업데이트", "En qué estoy", "En qué trabajo", "Últimas novedades",
    "Ce que je fais", "En cours", "Actualités", "Woran ich arbeite", "Aktuell in Arbeit",
    "Neuigkeiten", "No que estou focado", "Em andamento", "Atualizações recentes",
    "Чем я занимаюсь", "Сейчас в работе", "Последние обновления", "ما أعمل عليه",
    "آخر التحديثات"
  ],
  can_help: [
    "Ce que je partage", "Lo que puedo compartir", "O que posso compartilhar", "Was ich teilen kann", "What I can share", "Чем могу поделиться", "ما أستطيع مشاركته", "共有できるもの", "可以分享", "나눌 수 있는 것",
    "我能提供", "能解决的问题", "创作方向", "能解決的問題", "創作方向",
    "What I can offer", "Problems I solve", "What I create", "できること",
    "解決できる課題", "制作テーマ", "제공할 수 있는 것", "해결할 수 있는 문제",
    "창작 방향", "Qué puedo aportar", "Problemas que resuelvo", "Qué creo",
    "Ce que je peux apporter", "Problèmes résolus", "Ce que je crée",
    "Was ich beitragen kann", "Probleme, die ich löse", "Meine Themen",
    "O que posso oferecer", "Problemas que resolvo", "O que crio",
    "Чем могу быть полезен", "Какие задачи решаю", "Направление творчества",
    "ما يمكنني تقديمه", "المشكلات التي أحلها", "مجال إبداعي"
  ],
  contact_intent: [
    "想认识", "想交流", "合作方式", "想認識", "I'd like to meet", "Let's talk",
    "How I work", "出会いたい人", "話したいこと", "仕事の進め方", "만나고 싶은 사람",
    "이야기하고 싶은 것", "협업 방식", "A quién quiero conocer", "Hablemos",
    "Cómo trabajo", "Rencontres souhaitées", "Échangeons", "Ma façon de travailler",
    "Wen ich kennenlernen möchte", "Austausch", "Arbeitsweise", "Quem quero conhecer",
    "Vamos conversar", "Como trabalho", "С кем хочу познакомиться", "Обсудим",
    "Как я работаю", "من أود التعرف إليه", "لنتحدث", "طريقة العمل"
  ],
  work: [
    "Client projects", "Kundenprojekte", "Lo que he creado", "Meine Arbeiten", "Mes réalisations", "Minhas criações", "Projetos para clientes", "Projets clients", "Proyectos para clientes", "Things I made", "Клиентские проекты", "Мои работы", "أعمال أنجزتها", "مشاريع العملاء", "作ったもの", "做过的东西", "做過的東西", "制作事例", "專案案例", "项目案例", "만든 것들", "프로젝트 사례",
    "相关经历与案例", "相關經歷與案例", "Relevant experience and cases", "関連する経験と事例", "관련 경험과 사례", "Experiencia y casos relevantes", "Expériences et cas concrets", "Relevante Erfahrung und Projekte", "Experiências e casos relevantes", "Значимый опыт и кейсы", "خبرات وحالات ذات صلة",
    "做过的产品", "适合的合作", "从这里开始", "做過的產品", "適合的合作",
    "從這裡開始", "Products I've built", "Good-fit projects", "Start here",
    "これまでのプロダクト", "相性のよい仕事", "まずはこちら", "만든 제품",
    "잘 맞는 협업", "여기서 시작하세요", "Productos que he creado",
    "Colaboraciones ideales", "Empieza aquí", "Produits réalisés",
    "Collaborations adaptées", "Par où commencer", "Gebaute Produkte",
    "Passende Zusammenarbeit", "Hier anfangen", "Produtos que criei", "Boas parcerias",
    "Comece aqui", "Созданные продукты", "Подходящее сотрудничество", "С чего начать",
    "منتجات أنشأتها", "التعاون المناسب", "ابدأ من هنا"
  ]
};

const BODY_SECTION_KIND_BY_ALIAS = new Map<string, BodySectionKind>();
for (const kind of Object.keys(BODY_SECTION_ALIASES) as BodySectionKind[]) {
  for (const alias of BODY_SECTION_ALIASES[kind]) {
    BODY_SECTION_KIND_BY_ALIAS.set(alias.trim().toLowerCase(), kind);
  }
}

export function getBodySectionAliasKind(title: string): BodySectionKind | undefined {
  return BODY_SECTION_KIND_BY_ALIAS.get(title.trim().toLowerCase());
}
